import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DollarSign, Users, Home, Briefcase, Clock } from "lucide-react";
import { PURPOSE_OPTIONS, WizardFormData } from "@/types/wizard";

interface Step4PurposeProps {
  formData: WizardFormData;
  updateFormData: (updates: Partial<WizardFormData>) => void;
  isDark: boolean;
}

const iconMap: Record<string, React.ReactNode> = {
  rental: <DollarSign className="w-5 h-5" />,
  elderly_parents: <Users className="w-5 h-5" />,
  young_adults: <Users className="w-5 h-5" />,
  guest_house: <Home className="w-5 h-5" />,
  home_office: <Briefcase className="w-5 h-5" />,
  future_downsizing: <Clock className="w-5 h-5" />,
  other: <Home className="w-5 h-5" />,
};

const Step4Purpose = ({ formData, updateFormData, isDark }: Step4PurposeProps) => {
  const handlePurposeChange = (value: string, checked: boolean) => {
    if (checked) {
      updateFormData({ purposes: [...formData.purposes, value] });
    } else {
      updateFormData({ 
        purposes: formData.purposes.filter(p => p !== value),
        ...(value === "other" ? { otherPurpose: "" } : {}),
      });
    }
  };

  const cardStyles = {
    backgroundColor: isDark ? "#0a0a0a" : "#ffffff",
    boxShadow: isDark
      ? "4px 4px 8px #000000, -4px -4px 8px #141414"
      : "4px 4px 8px #d1d1d1, -4px -4px 8px #ffffff",
  };

  const inputStyles = isDark
    ? "bg-[#0a0a0a] border-0 text-white shadow-[inset_4px_4px_8px_#000000,inset_-4px_-4px_8px_#141414] rounded-xl"
    : "bg-white border-0 text-gray-900 shadow-[inset_4px_4px_8px_#d1d1d1,inset_-4px_-4px_8px_#ffffff] rounded-xl";

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 
          className="text-2xl md:text-3xl font-bold mb-2"
          style={{ color: "#E4683B" }}
        >
          What's the purpose of your ADU?
        </h2>
        <p 
          className="text-lg opacity-80"
          style={{ color: isDark ? "#ffffff" : "#1a1a1a" }}
        >
          Select all that apply
        </p>
      </div>

      <div className="max-w-2xl mx-auto space-y-3">
        {PURPOSE_OPTIONS.map((option) => {
          const isChecked = formData.purposes.includes(option.value);
          
          return (
            <div key={option.value}>
              <div 
                className="flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-all duration-200 hover:scale-[1.01]"
                style={{
                  ...cardStyles,
                  borderLeft: isChecked ? "4px solid #E4683B" : "4px solid transparent",
                }}
                onClick={() => handlePurposeChange(option.value, !isChecked)}
              >
                <Checkbox
                  id={option.value}
                  checked={isChecked}
                  onCheckedChange={(checked) => handlePurposeChange(option.value, checked as boolean)}
                  className="border-2"
                  style={{ 
                    borderColor: isDark ? "#444" : "#ccc",
                    backgroundColor: isChecked ? "#E4683B" : "transparent",
                  }}
                />
                <div 
                  className="flex-shrink-0"
                  style={{ color: "#E4683B" }}
                >
                  {iconMap[option.value]}
                </div>
                <Label 
                  htmlFor={option.value} 
                  className="flex-1 cursor-pointer text-base"
                  style={{ color: isDark ? "#ffffff" : "#1a1a1a" }}
                >
                  {option.label}
                </Label>
              </div>
              
              {/* Show input for "Other" option */}
              {option.value === "other" && isChecked && (
                <div className="mt-3 ml-12">
                  <Input
                    placeholder="Please specify your purpose..."
                    value={formData.otherPurpose}
                    onChange={(e) => updateFormData({ otherPurpose: e.target.value })}
                    className={inputStyles}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Step4Purpose;
